import * as pdfjsLib from 'pdfjs-dist';

/**
 * 加載 PDF 文檔
 * @param {File} file - PDF 文件
 * @returns {Promise} PDF 文檔對象
 */
export const loadPdfDocument = async (file) => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const typedArray = new Uint8Array(arrayBuffer);
    const pdf = await pdfjsLib.getDocument({ data: typedArray }).promise;
    return pdf;
  } catch (error) {
    console.error('PDF 加載錯誤:', error);
    throw new Error('PDF 文件加載失敗');
  }
};

/**
 * 渲染 PDF 頁面到 Canvas
 * @param {object} page - PDF 頁面對象
 * @param {number} scale - 縮放比例
 * @returns {Promise<HTMLCanvasElement>} Canvas 元素
 */
export const renderPageToCanvas = async (page, scale = 1.5) => {
  try {
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    
    canvas.height = viewport.height;
    canvas.width = viewport.width;
    
    // 設置白色背景（用於 JPG 格式）
    context.fillStyle = 'white';
    context.fillRect(0, 0, canvas.width, canvas.height);
    
    const renderContext = {
      canvasContext: context,
      viewport: viewport
    };
    
    await page.render(renderContext).promise;
    return canvas;
  } catch (error) {
    console.error('頁面渲染錯誤:', error);
    throw new Error('頁面渲染失敗');
  }
};

/**
 * 將 Canvas 轉換為 Blob
 * @param {HTMLCanvasElement} canvas - Canvas 元素
 * @param {string} format - 輸出格式
 * @param {number} quality - 圖片質量
 * @returns {Promise<Blob>} 圖片 Blob
 */
export const canvasToBlob = (canvas, format = 'image/jpeg', quality = 0.8) => {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Canvas 轉換失敗'));
      }
    }, format, quality);
  });
};

/**
 * 批量轉換 PDF 頁面為圖片
 * @param {object} pdfDocument - PDF 文檔對象
 * @param {number[]} pageNumbers - 要轉換的頁碼數組
 * @param {string} format - 輸出格式
 * @param {number} quality - 圖片質量
 * @param {Function} onProgress - 進度回調
 * @returns {Promise<Array>} 轉換結果數組
 */
export const convertPdfPagesToImages = async (
  pdfDocument, 
  pageNumbers, 
  format = 'image/jpeg', 
  quality = 0.8,
  onProgress = null
) => {
  const results = [];
  
  for (let i = 0; i < pageNumbers.length; i++) {
    const pageNum = pageNumbers[i];
    
    try {
      // 更新進度
      if (onProgress) {
        onProgress(i + 1, pageNumbers.length, pageNum);
      }
      
      // 獲取頁面
      const page = await pdfDocument.getPage(pageNum);
      
      // 渲染到 Canvas
      const canvas = await renderPageToCanvas(page);
      
      // 轉換為 Blob
      const blob = await canvasToBlob(canvas, format, quality);
      
      // 創建 URL
      const url = URL.createObjectURL(blob);
      
      results.push({
        pageNumber: pageNum,
        blob,
        url,
        canvas
      });
      
    } catch (error) {
      console.error(`轉換第 ${pageNum} 頁時出錯:`, error);
      // 繼續處理下一頁
      continue;
    }
  }
  
  return results.sort((a, b) => a.pageNumber - b.pageNumber);
};